// Pure per-update action state: edge detection, press buffering and the
// "suppress until released" resync used after pauses, overlays and room
// restarts. Devices (keyboard, mouse, gamepad) only feed raw booleans and
// discrete press latches; every rule about what counts as a press lives here
// so it can be unit-tested without Phaser.

export class ActionStates<A extends string> {
  readonly actions: readonly A[];
  /** Raw physical state per action index. The owner fills it before each step(). */
  readonly raw: boolean[];

  private readonly index = new Map<A, number>();
  /** Level actions (movement directions) keep reading as held after a resync. */
  private readonly level: boolean[];
  private readonly heldNow: boolean[];
  private readonly pressedNow: boolean[];
  private readonly releasedNow: boolean[];
  private readonly since: number[];
  /** Held through a resync: reads as released until physically released (or freshly pressed). */
  private readonly suppressed: boolean[];
  /** Discrete down events since the last step (catches taps shorter than a frame). */
  private readonly latched: boolean[];

  constructor(actions: readonly A[], levelActions: readonly A[] = []) {
    this.actions = actions;
    const n = actions.length;
    actions.forEach((a, i) => this.index.set(a, i));
    this.raw = new Array<boolean>(n).fill(false);
    this.level = actions.map((a) => levelActions.includes(a));
    this.heldNow = new Array<boolean>(n).fill(false);
    this.pressedNow = new Array<boolean>(n).fill(false);
    this.releasedNow = new Array<boolean>(n).fill(false);
    this.since = new Array<number>(n).fill(Infinity);
    this.suppressed = new Array<boolean>(n).fill(false);
    this.latched = new Array<boolean>(n).fill(false);
  }

  indexOf(a: A): number {
    return this.index.get(a) ?? -1;
  }

  /** Record a discrete press (key down, mouse down, wheel notch); reported on the next step(). */
  latch(a: A): void {
    const i = this.indexOf(a);
    if (i >= 0) this.latched[i] = true;
  }

  /**
   * Advance one update. pressed/released are true for exactly this update.
   * When disabled nothing is held, pressed or released (presses are dropped).
   */
  step(dt: number, enabled: boolean): void {
    const n = this.actions.length;
    for (let i = 0; i < n; i++) {
      let phys = this.raw[i];
      let latch = this.latched[i];
      this.latched[i] = false;

      if (!enabled) {
        this.heldNow[i] = false;
        this.pressedNow[i] = false;
        this.releasedNow[i] = false;
        this.since[i] += dt;
        continue;
      }

      if (this.suppressed[i]) {
        if (latch) {
          // A genuinely new press after the resync (latches were cleared by it).
          this.suppressed[i] = false;
        } else if (!phys) {
          this.suppressed[i] = false;
        } else {
          phys = false;
          latch = false;
        }
      }

      const down = phys || latch;
      const wasDown = this.heldNow[i];
      // A latch while already held is a release + re-press inside one frame.
      const pressed = down && (!wasDown || latch);
      this.pressedNow[i] = pressed;
      this.releasedNow[i] = wasDown && !down;
      this.heldNow[i] = down;
      if (pressed) this.since[i] = 0;
      else this.since[i] += dt;
    }
  }

  /**
   * Adopt the current physical state without producing edges: nothing reads as
   * pressed, buffered presses are forgotten, and non-level actions that are
   * physically held stay suppressed until they are released.
   */
  resync(): void {
    const n = this.actions.length;
    for (let i = 0; i < n; i++) {
      const phys = this.raw[i];
      this.latched[i] = false;
      this.pressedNow[i] = false;
      this.releasedNow[i] = false;
      this.since[i] = Infinity;
      if (this.level[i]) {
        this.suppressed[i] = false;
        this.heldNow[i] = phys;
      } else {
        this.suppressed[i] = phys;
        this.heldNow[i] = false;
      }
    }
  }

  /** Drop every held/pressed state (used while disabled so re-enabling starts clean). */
  clear(): void {
    this.heldNow.fill(false);
    this.pressedNow.fill(false);
    this.releasedNow.fill(false);
    this.latched.fill(false);
  }

  held(a: A): boolean {
    const i = this.indexOf(a);
    return i >= 0 && this.heldNow[i];
  }

  pressed(a: A): boolean {
    const i = this.indexOf(a);
    return i >= 0 && this.pressedNow[i];
  }

  released(a: A): boolean {
    const i = this.indexOf(a);
    return i >= 0 && this.releasedNow[i];
  }

  sincePressed(a: A): number {
    const i = this.indexOf(a);
    return i >= 0 ? this.since[i] : Infinity;
  }

  /** Forget a buffered press (and hide it from pressed() for the rest of this update). */
  consume(a: A): void {
    const i = this.indexOf(a);
    if (i < 0) return;
    this.since[i] = Infinity;
    this.pressedNow[i] = false;
  }

  heldAt(i: number): boolean {
    return this.heldNow[i];
  }
}
